<template>
  <div
    v-if="editor"
    class="html-editor text-font text-sm-body-medium text-body-large"
    data-testid="html-editor"
    :class="{ disabled }"
  >
    <html-editor-toolbar
      :is-paragraph
      :is-heading4
      :is-heading5
      :is-heading6
      :is-bullet-list
      :is-ordered-list
      :is-link
      :is-table
      :can-undo
      :can-redo
      :can-indent
      :can-remove-indent
      :alignment
      :disabled
      :link-href
      :link-target
      :current-color="currentColor"
      @set-heading="run('setHeading', { level: $event })"
      @set-paragraph="run('setParagraph')"
      @remove-link="removeLink"
      @toggle-bold="run('toggleBold')"
      @toggle-italic="run('toggleItalic')"
      @toggle-text-align="run('toggleTextAlign', $event)"
      @toggle-bullet-list="run('toggleBulletList')"
      @toggle-ordered-list="run('toggleOrderedList')"
      @add-horizontal-rule="run('setHorizontalRule')"
      @undo="run('undo')"
      @redo="run('redo')"
      @insert-table="run('insertTable', { rows: 2, cols: 3, withHeaderRow: true })"
      @delete-table="run('deleteTable')"
      @add-column-before="run('addColumnBefore')"
      @add-column-after="run('addColumnAfter')"
      @delete-column="run('deleteColumn')"
      @add-row-before="run('addRowBefore')"
      @add-row-after="run('addRowAfter')"
      @delete-row="run('deleteRow')"
      @indent="run('indent')"
      @outdent="run('outdent')"
    />
    <v-sheet
      border="primary md"
    >
      <editor-content

        :editor
        class="nolinks multi-line"
      />
    </v-sheet>
    <bubble-menu
      v-if="editor"
      :editor="editor"
      :options="{ placement: 'bottom', offset: 8 }"
    >
      <div class="bubble-menu">
        <v-btn-group
          base-color="primary"
          color="primary-darken-1"
          style="height: 40px"
        >
          <v-btn
            icon="fas fa-bold"
            :active="!isBold"
            :disabled="disabled"
            size="xs"
            @click="editor.chain().focus().toggleBold().run()"
          />
          <v-btn
            icon="fas fa-italic"
            :active="!isItalic"
            :disabled="disabled"
            size="xs"
            @click="editor.chain().focus().toggleItalic().run()"
          />
          <v-btn
            icon="fas fa-underline"
            :active="!isUnderlined"
            :disabled="disabled"
            size="xs"
            @click="editor.chain().focus().toggleUnderline().run()"
          />
          <v-btn
            v-tooltip="isLink ? t('html_editor.link_edit') : t('html_editor.link_add')"
            icon="fas fa-link"
            color="primary-darken-1"
            :active="!isLink"
            :disabled="disabled"
            rounded="0"
            size="xs"
            :aria-label="isLink ? t('html_editor.link_edit') : t('html_editor.link_add')"
            @click="linkInputDialogVisible = true"
          />
          <v-speed-dial
            location="bottom center"
            transition="fade-transition"
          >
            <template #activator="{ props: activatorProps }">
              <v-btn
                v-tooltip="t('html_editor.text_color')"
                v-bind="activatorProps"
                :size="size"
                :active="colorIsSet"
                rounded="0"
                color="primary-darken-1"
                :aria-label="t('html_editor.text_color')"
                :icon="currentColor ? 'fas fa-palette' : 'fas fa-palette'"
                :style="currentColor ? `--v-theme-overlay-multiplier:1; color:${currentColor}` : ''"
              />
            </template>
            <v-btn
              v-for="c in colors"
              :key="c"
              :style="`background:${c}; color: ${(c)}`"
              :size="size"
              icon="fas fa-circle"
              @click="applyColor(c)"
            />
            <v-btn
              key="clear"
              v-tooltip="t('html_editor.remove_color')"
              elevation="5"
              color="primary-darken-1"
              :size="size"
              icon="fas fa-ban"
              :aria-label="t('html_editor.remove_color')"
              @click="unsetColor"
            />
          </v-speed-dial>
        </v-btn-group>
      </div>
    </bubble-menu>
    <input-prompt-dialog
      v-model="linkInputDialogVisible"
      :title="t('html_editor.link_add_prompt')"
      :initial-value="linkHref"
      required
      @close="onLinkInputClose"
    >
      <v-checkbox
        v-model="openInNewTab"
        class="text-font mt-n4 mb-n4"
        :label="t('editor.open_in_new_tab')"
      />
    </input-prompt-dialog>
  </div>
</template>

<script lang="ts" setup>
import type { TextAlignment } from './types';
import Paragraph from '@tiptap/extension-paragraph';
import { TableCell, TableHeader, TableRow } from '@tiptap/extension-table';
import TextAlign from '@tiptap/extension-text-align';
import { Color, TextStyle } from '@tiptap/extension-text-style';
import StarterKit from '@tiptap/starter-kit';
import { Editor, EditorContent } from '@tiptap/vue-3';
import { BubbleMenu } from '@tiptap/vue-3/menus';
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useDisplay, useTheme } from 'vuetify';
import * as vuetifyColors from 'vuetify/util/colors';
import InputPromptDialog from '@/components/dialogs/InputPromptDialog.vue';
import { useSettings } from '@/composables';
import { CustomHeading } from './CustomHeading';
import HtmlEditorToolbar from './HtmlEditorToolbar.vue';
import { CustomImage } from './tiptap-extension-image';
import { Indent } from './tiptap-extension-indent';
import { TableResponsive } from './tiptap-extension-table-responsive';

;

const props = defineProps({
  disabled: {
    type: Boolean,
    default: false,
  },
});
const openInNewTab = ref(false);

const { t } = useI18n();
const linkInputDialogVisible = ref(false);
function onLinkInputClose(value?: string) {
  let url: string;
  if (value) {
    if (!/^https?:\/\//i.test(value)) {
      url = `https://${value}`;
    }
    else {
      url = value;
    }

    setLink(url, openInNewTab.value ? '_blank' : '_self');
  }
}
const model = defineModel({
  type: String,
  default: '',
});

const editor = new Editor({
  extensions: [
    StarterKit.configure({
      heading: false, // We are extending it, so disable here
      paragraph: false, // We are extending it, so disable here
      link: {
        openOnClick: false,
        defaultProtocol: 'https',
        linkOnPaste: true,
        autolink: true,
      },
    }),
    Color,
    TextStyle,
    TextAlign.configure({
      types: ['heading', 'paragraph'],
    }),
    CustomHeading.configure({
      levels: [1, 2, 3, 4, 5, 6],
    }),
    CustomImage,
    TableResponsive.configure({
      resizable: true,
    }),
    TableCell,
    TableRow,
    Paragraph.configure({
      HTMLAttributes: {
        class: 'text-body-large',
      },
    }),
    TableHeader,
    Indent.configure({
      types: ['paragraph'],
    }),
  ],
  content: model.value,
  editable: !props.disabled,
  onUpdate: () => {
    model.value = editor.getHTML();
  },
});

const isBold = computed(() => {
  return editor.isActive('bold');
});
const isUnderlined = computed(() => {
  return editor.isActive('underline');
});
const isItalic = computed(() => {
  return editor.isActive('italic');
});
const isParagraph = computed(() => {
  return editor.isActive('paragraph');
});
const isHeading4 = computed(() => {
  return editor.isActive('heading', { level: 4 });
});
const colorIsSet = computed(() => {
  return editor.isActive('textStyle') && !!editor.getAttributes('textStyle').color;
});
const isHeading5 = computed(() => {
  return editor.isActive('heading', { level: 5 });
});
const isHeading6 = computed(() => {
  return editor.isActive('heading', { level: 6 });
});
const isBulletList = computed(() => {
  return editor.isActive('bulletList');
});
const isOrderedList = computed(() => {
  return editor.isActive('orderedList');
});
const isLink = computed(() => {
  return !props.disabled && editor.isActive('link');
});
const canUndo = computed(() => {
  return !props.disabled && editor.can().undo();
});
const canRedo = computed(() => {
  return !props.disabled && editor.can().redo();
});
const isTable = computed(() => {
  return editor.isActive('table');
});
const alignment = computed<TextAlignment | undefined>(() => {
  if (!editor)
    return undefined;
  const aligns: Array<TextAlignment> = ['left', 'center', 'right', 'justify'];
  return aligns.find(a => editor.isActive({ textAlign: a })) || undefined;
});
const canIndent = computed(() => {
  return !isBulletList.value && !isOrderedList.value && editor.isActive('paragraph');
});
const canRemoveIndent = computed(() => {
  return editor.getAttributes('paragraph')?.indent > 0;
});
const linkHref = computed(() => {
  return editor.getAttributes('link')?.href || '';
});
const linkTarget = computed(() => {
  return editor.getAttributes('link')?.target || '_self';
});
const currentColor = computed(() => {
  return editor.getAttributes('textStyle')?.color || '';
});

watch(
  () => model.value,
  (value) => {
    if (editor) {
      const isSame = editor.getHTML() === value;
      if (isSame) {
        return;
      }
      editor.commands.setContent(value, { emitUpdate: false });
    }
  },
);

watch(
  () => props.disabled,
  (value) => {
    if (editor) {
      editor.setOptions({
        editable: !value,
      });
    }
  },
);

onBeforeUnmount(() => {
  if (editor) {
    editor.destroy();
  }
});

function run<K extends keyof typeof editor.commands>(
  command: K,
  ...args: Parameters<typeof editor.commands[K]>
): void {
  // Expect error is because of the arguments spread "...args"
  // @ts-expect-error: dynamic method invocation
  editor.chain().focus()[command](...args).run();
}

function selectCurrentWord() {
  const { from: cursorPosition } = editor.state.selection;
  const node = editor.state.doc.nodeAt(cursorPosition);

  if (!node?.text)
    return;

  const offset = cursorPosition - editor.state.doc.resolve(cursorPosition).start();
  const left = node.text.slice(0, offset).search(/\S+$/);
  const right = (node.text.slice(offset).match(/^\S+/) || [''])[0].length;

  editor.commands.setTextSelection({ from: cursorPosition - (offset - left), to: cursorPosition + right });
}

function setLink(href: string, targetType = '_self') {
  if (href) {
    editor.chain().focus().extendMarkRange('link').run();
    if (editor.state.selection.empty) {
      selectCurrentWord();
      editor.chain().focus().setLink({ href, target: targetType }).run();
    }
    else {
      editor.chain().focus().extendMarkRange('link').setLink({ href, target: targetType }).run();
    }
  }
}

function removeLink() {
  editor.chain().focus().extendMarkRange('link').unsetLink().run();
}

function applyColor(color: string) {
  if (!color)
    return;
  editor.commands.setColor(color);
}

function unsetColor() {
  editor.commands.unsetColor();
}
const { getSetting } = useSettings();

const { xs } = useDisplay();
const size = computed(() => (xs.value ? 'x-small' : 'small'));
const currentTheme = getSetting('theme');

const theme = useTheme();
const colors = computed(() => {
  const currentThemeName = currentTheme === 'default' ? 'sapphireTheme' : `${currentTheme}Theme`;
  const themeColors = theme.themes.value[currentThemeName]?.colors || {} as Record<string, string>;
  const orderedKeys = [
    'primary',
    'primary-darken-1',
    'primary-darken-2',
    'primary-lighten-1',
    'info',
    'success',
    'warning',
    'error',
    'font',
  ];
  const derived = orderedKeys.map(k => themeColors[k]).filter(Boolean).map(String);
  // Ensure at least a couple of colors exist so the picker renders usefully.
  return derived.length > 0
    ? derived
    : [
        String(vuetifyColors.grey.darken2),
        String(vuetifyColors.red.base),
      ];
});
</script>

<style lang="scss" scoped>
.multi-line {
  max-height: 60vh;
  overflow-y: auto;
}
.nolinks :deep(a) {
  pointer-events: none;      /* disables mouse click */
  cursor: text;              /* indicates editing */
  text-decoration: underline;/* keep visual cue, optional */
}
/* Bubble menu */
.bubble-menu {
  // background-color: white;
  // border: 1px solid black;
  // border-radius: 0.7rem;
  // box-shadow: var(--shadow);
  // display: flex;
  // padding: 0.2rem;
  z-index: 100;
}
</style>
